import {
  faArrowsRotate,
  faCheck,
  faChevronDown,
  faChevronUp,
  faSpinner,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useAssignmentGetAutoApproveChecksForCase,
  useAssignmentRefreshAutoApproveChecks,
} from 'api/assignment/assignment';
import { AssignmentStatusEnum } from 'api/model';
import Dropdown from 'components/Dropdown';
import AiSparkle from 'components/icons/AiSparkle';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { formatDate, formatTime } from 'utils/date-helpers';
import {
  buildCheckItems,
  getGraceText,
  getVerdict,
  getVerdictMeta,
  OPTED_OUT_TEXT,
  TONE,
  VERDICT_CONFIG,
} from './ApprovalChecksWidget';

const DropdownWrapper = styled(Dropdown)`
  width: 100%;
`;

const DropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  min-height: 32px;
  font-size: 12px;
`;

const TriggerVerdict = styled.span<{ tone: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${({ tone }) => tone};
`;

const TriggerMeta = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const DropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  max-height: 460px;
  overflow-y: auto;
  font-size: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const DeviationRow = styled.div`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  border-left: 3px solid ${TONE.bad};
  background-color: ${TONE.bad}14;
  color: ${({ theme }) => theme.colors.foreground.primary};
  white-space: pre-wrap;
`;

const InfoRow = styled.div<{ tone: string }>`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  border-left: 3px solid ${({ tone }) => tone};
  background-color: ${({ tone }) => tone}14;
  color: ${({ theme }) => theme.colors.foreground.primary};
  white-space: pre-wrap;
`;

const CheckList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const LinkButton = styled.button.attrs({ type: 'button' })`
  align-self: flex-start;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.background.button};
`;

const GhostButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground.secondary};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const formatDateTime = (d: Date | string) => {
  const date = new Date(d);
  return `${formatDate(date)} kl. ${formatTime(date)}`;
};

interface Props {
  caseId: number;
  assignmentId: number;
  assignmentStatusID: AssignmentStatusEnum;
  autoApprove?: boolean | null;
  autoApprovedAt?: Date | string | null;
}

/**
 * "Autogodkännande" i ärendemodalen: senaste snapshotten från autogodkännandet för ett
 * uppdrag — utslaget, avvikelserna som hindrar godkännandet och de kontroller som gick
 * igenom. Fälls ut som "Fakturera extra". Endast Efterkontroll och SuperAdmin.
 */
const AutoApproveCheckDropdown = ({
  caseId,
  assignmentId,
  assignmentStatusID,
  autoApprove,
  autoApprovedAt,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedChecks, setExpandedChecks] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Hämtar alla ben i ärendet i ett anrop; övriga ben delar samma query-nyckel.
  const {
    data: checks,
    isLoading,
    isError,
    refetch,
  } = useAssignmentGetAutoApproveChecksForCase({ caseId });
  const data = checks?.find((c) => c.assignmentID === assignmentId);
  const refreshMutation = useAssignmentRefreshAutoApproveChecks();

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!containerRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      await refreshMutation.mutateAsync({ data: [assignmentId] });
      await refetch();
    } catch {
      toast.error('Kunde inte uppdatera kontrollen');
    }
  }, [assignmentId, refreshMutation, refetch]);

  const result = data?.result ?? null;
  const verdict = getVerdict(
    result,
    assignmentStatusID,
    autoApprovedAt,
    autoApprove
  );
  const config = VERDICT_CONFIG[verdict];
  const triggerMeta = getVerdictMeta(verdict, result, autoApprovedAt);
  // Utan data vet vi inte om uppdraget kontrollerats — visa aldrig "Inte kontrollerad"
  // för ett anrop som laddar eller misslyckades.
  // Godkänt/avgodkänt avgörs av uppdraget självt och behöver ingen snapshot.
  const showUnknown =
    (isLoading || isError) &&
    (verdict === 'not-checked' ||
      verdict === 'approvable' ||
      verdict === 'not-approvable');

  const toggleChecks = (name: string) =>
    setExpandedChecks((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Ett godkänt uppdrag ska inte få sin snapshot omskriven — den visar vad snurran såg.
  const canRefresh = assignmentStatusID !== AssignmentStatusEnum.Approved;
  const isRefreshing = refreshMutation.status === 'loading';

  const renderGraceInfo = () => {
    if (verdict === 'opted-out') {
      return <InfoRow tone={TONE.neutral}>{OPTED_OUT_TEXT}</InfoRow>;
    }
    if (verdict !== 'approvable' || !data) {
      return null;
    }
    return (
      <InfoRow tone={data.gracePeriodPassed ? TONE.good : TONE.neutral}>
        {getGraceText(data.gracePeriodPassed, data.autoApproveEligibleAt)}
      </InfoRow>
    );
  };

  const dropdownContent = (
    <DropdownContent>
      <Header>
        <span>
          {(() => {
            if (isLoading) return 'Laddar…';
            if (isError) return 'Kunde inte hämta autogodkännandets kontroll.';
            if (!data?.checkedAt)
              return 'Uppdraget har inte kontrollerats av autogodkännandet ännu.';
            return `Kontrollerad ${formatDateTime(data.checkedAt)}${
              data.isFrozen ? ' (fryst, uppdateras inte längre av snurran)' : ''
            }`;
          })()}
        </span>
        {canRefresh && (
          <GhostButton
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            title="Kör om autogodkännandets kontroller för uppdraget och spara resultatet"
          >
            <FontAwesomeIcon
              icon={isRefreshing ? faSpinner : faArrowsRotate}
              spin={isRefreshing}
            />
            Uppdatera kontroll
          </GhostButton>
        )}
      </Header>

      {renderGraceInfo()}

      {result &&
        buildCheckItems(result).map((item) => (
          <Section key={item.name}>
            <SectionTitle>
              <FontAwesomeIcon
                icon={item.hasDeviations ? faXmark : faCheck}
                color={item.hasDeviations ? TONE.bad : TONE.good}
              />
              {item.name}
              {!item.hasDeviations && ': OK'}
            </SectionTitle>
            {item.deviations.map((deviation, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <DeviationRow key={i}>{deviation}</DeviationRow>
            ))}
            {item.checks.length > 0 && (
              <>
                <LinkButton onClick={() => toggleChecks(item.name)}>
                  {expandedChecks.has(item.name)
                    ? 'Dölj kontroller'
                    : `Visa ${item.checks.length} kontroll${
                        item.checks.length > 1 ? 'er' : ''
                      }`}
                </LinkButton>
                {expandedChecks.has(item.name) && (
                  <CheckList>
                    {item.checks.map((check, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>{check}</li>
                    ))}
                  </CheckList>
                )}
              </>
            )}
          </Section>
        ))}

      {result && result.qualityFlags.length > 0 && (
        <Section>
          <SectionTitle>Anmärkningar (blockerar inte)</SectionTitle>
          {result.qualityFlags.map((flag, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <InfoRow key={i} tone={TONE.neutral}>
              {flag.message}
            </InfoRow>
          ))}
        </Section>
      )}

      {result && result.passengerBreakPlacements.length > 0 && (
        <Section>
          <SectionTitle>Rast som skrivs vid godkännande</SectionTitle>
          {result.passengerBreakPlacements.map((p) => (
            <InfoRow key={p.assignmentID} tone={TONE.auto}>
              {`Uppdrag ${p.assignmentID}: ${p.fromMinutes} → ${p.toMinutes} min`}
            </InfoRow>
          ))}
        </Section>
      )}
    </DropdownContent>
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div ref={containerRef} onClick={handleContainerClick}>
      <DropdownWrapper
        capWidthToWrapper
        content={isOpen ? dropdownContent : undefined}
        onLostFocus={() => setIsOpen(false)}
        onClick={() => setIsOpen(!isOpen)}
      >
        <DropdownTrigger>
          {showUnknown ? (
            <TriggerVerdict tone={TONE.neutral}>
              {isLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faTriangleExclamation} />
              )}
              {isLoading ? 'Laddar…' : 'Kunde inte hämta kontrollen'}
            </TriggerVerdict>
          ) : (
            <span>
              <TriggerVerdict tone={config.color}>
                {verdict === 'auto-approved' ? (
                  <AiSparkle title="Autogodkänd" />
                ) : (
                  config.icon && <FontAwesomeIcon icon={config.icon} />
                )}
                {config.label}
              </TriggerVerdict>
              {triggerMeta && <TriggerMeta> · {triggerMeta}</TriggerMeta>}
            </span>
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            size="xs"
          />
        </DropdownTrigger>
      </DropdownWrapper>
    </div>
  );
};

export default AutoApproveCheckDropdown;
